import React, { useState, useEffect } from 'react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import { XMarkIcon, InformationCircleIcon, CogIcon, CubeIcon, ChartBarIcon } from '@heroicons/react/24/outline';

interface MqttConfigModalProps {
  device: any;
  onClose: () => void;
}

interface LineSettings {
  id: string;
  lineName: string;
  hardwareId: number;
  numberOfStations: number;
  // Infeed Settings
  conveyorStartDelay: number;
  conveyorStopDelay: number;
  bufferHopperOpenDelay: number;
  bufferHopperCloseDelay: number;
  weighingDelay: number;
  zeroingDelay: number;
  zeroThresholdWeight: number;
  hopperCapacity: number;
  defaultInfeedTargetWeight: number;
  armTimingSettings: any;
  // Outfeed Settings
  outfeedHopperCapacity: number;
  outfeedCycleWaitTime: number;
  outfeedWeighDelay: number;
  outfeedZeroDelay: number;
  outfeedZeroThresholdWeight: number;
  outfeedBreakoutAttempts: number;
  outfeedPocketBufferHopperCloseDelay: number;
  pocketCloseDelays: any;
}

const MqttConfigModal: React.FC<MqttConfigModalProps> = ({ device, onClose }) => {
  const { apiUrl } = useDatabaseContext();
  const [lineSettings, setLineSettings] = useState<LineSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const isInfeed = device.node_type === 1;
  const isOutfeed = device.node_type === 2;
  const lineNumber = device.line_number;

  useEffect(() => {
    fetchLineSettings();
  }, [device]);

  const fetchLineSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all lines to find the one matching the hardware's line number
      const response = await fetch(`${apiUrl}/lines`);
      if (!response.ok) {
        throw new Error('Failed to fetch line settings');
      }
      
      const data = await response.json();
      const lines = data.data || [];
      
      // Find the line that matches this hardware's line number
      const matchingLine = lines.find((line: LineSettings) => 
        line.hardwareId === lineNumber
      );
      
      if (matchingLine) {
        setLineSettings(matchingLine);
      } else {
        setError(`No line configuration found for Line ${lineNumber}`);
      }
    } catch (err) {
      console.error('Error fetching line settings:', err);
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const parseArmTimingSettings = (settings: any) => {
    if (!settings) return [];
    
    if (typeof settings === 'string') {
      try {
        const parsed = JSON.parse(settings);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    
    if (Array.isArray(settings)) {
      return settings;
    }
    
    if (typeof settings === 'object') {
      const keys = Object.keys(settings);
      if (keys.length === 0) {
        return [];
      }
      const values = Object.values(settings);
      if (values.every(v => typeof v === 'object' && v !== null)) {
        return values;
      }
    }
    
    return [];
  };

  const parsePocketDelays = (delays: any) => {
    if (!delays) return [];
    if (typeof delays === 'string') {
      try {
        return JSON.parse(delays);
      } catch {
        return [];
      }
    }
    if (Array.isArray(delays)) {
      return delays;
    }
    return [];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-slate-900 flex items-center gap-2">
            {isInfeed ? (
              <>
                <CubeIcon className="h-5 w-5 text-blue-600" />
                Infeed Hardware Configuration
              </>
            ) : isOutfeed ? (
              <>
                <ChartBarIcon className="h-5 w-5 text-green-600" />
                Outfeed Hardware Configuration
              </>
            ) : (
              <>
                <CogIcon className="h-5 w-5 text-slate-600" />
                Hardware Configuration
              </>
            )}
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
          {loading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-slate-200 rounded w-1/3"></div>
              <div className="h-32 bg-slate-200 rounded"></div>
            </div>
          ) : error ? (
            <div className="text-red-600 text-center p-8">
              <InformationCircleIcon className="h-12 w-12 mx-auto mb-4" />
              <p>{error}</p>
            </div>
          ) : lineSettings ? (
            <div className="space-y-6">
              {/* Device Information */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-medium text-slate-900 mb-3">Device Information</h4>
                <dl className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <dt className="text-slate-500">Device Name:</dt>
                    <dd className="font-medium">{device.device_name}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Line:</dt>
                    <dd className="font-medium">{lineSettings.lineName} (Hardware ID: {lineSettings.hardwareId})</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Device Type:</dt>
                    <dd className="font-medium">{isInfeed ? 'Infeed' : isOutfeed ? 'Outfeed' : 'Unknown'}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Station Config:</dt>
                    <dd className="font-medium">
                      {isInfeed ? `Hopper ${device.station_config}` : 
                       isOutfeed ? `Stations ${((device.station_config - 1) * 4 + 1)}-${device.station_config * 4}` :
                       device.station_config}
                    </dd>
                  </div>
                  {(isInfeed || isOutfeed) && (
                    <div>
                      <dt className="text-slate-500">Number of Stations:</dt>
                      <dd className="font-medium">{lineSettings.numberOfStations} stations</dd>
                    </div>
                  )}
                  {isOutfeed && (
                    <div>
                      <dt className="text-slate-500">Controlled Stations:</dt>
                      <dd className="font-medium">
                        {(() => {
                          const startStation = (device.station_config - 1) * 4 + 1;
                          const endStation = device.station_config * 4;
                          const stations = [];
                          for (let i = startStation; i <= endStation; i++) {
                            stations.push(i);
                          }
                          return `Stations ${stations.join(', ')} (${stations.length} total)`;
                        })()}
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-slate-500">Line Production Status:</dt>
                    <dd className="font-medium">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                        device.line_production_status === 'PRODUCTION' 
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : device.line_production_status === 'STOP'
                          ? 'bg-red-50 text-red-700 border-red-200'
                          : device.line_production_status === 'MAINTENANCE'
                          ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                          : device.line_production_status === 'PREPARE'
                          ? 'bg-blue-50 text-blue-700 border-blue-200'
                          : device.line_production_status === 'CLEAN'
                          ? 'bg-purple-50 text-purple-700 border-purple-200'
                          : 'bg-gray-50 text-gray-700 border-gray-200'
                      }`}>
                        {device.line_production_status || 'UNKNOWN'}
                      </span>
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Configuration Settings */}
              {isInfeed ? (
                <>
                  {/* Infeed Settings */}
                  <div>
                    <h4 className="font-medium text-slate-900 mb-4">Weigh Hopper Settings</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="bg-white border border-slate-200 p-3 rounded">
                        <dt className="text-sm text-slate-600">Conveyor Start Delay</dt>
                        <dd className="font-semibold text-slate-900">{lineSettings.conveyorStartDelay || 0} ms</dd>
                      </div>
                      <div className="bg-white border border-slate-200 p-3 rounded">
                        <dt className="text-sm text-slate-600">Conveyor Stop Delay</dt>
                        <dd className="font-semibold text-slate-900">{lineSettings.conveyorStopDelay || 0} ms</dd>
                      </div>
                      <div className="bg-white border border-slate-200 p-3 rounded">
                        <dt className="text-sm text-slate-600">Buffer Hopper Open Delay</dt>
                        <dd className="font-semibold text-slate-900">{lineSettings.bufferHopperOpenDelay || 0} ms</dd>
                      </div>
                      <div className="bg-white border border-slate-200 p-3 rounded">
                        <dt className="text-sm text-slate-600">Buffer Hopper Close Delay</dt>
                        <dd className="font-semibold text-slate-900">{lineSettings.bufferHopperCloseDelay || 0} ms</dd>
                      </div>
                      <div className="bg-white border border-slate-200 p-3 rounded">
                        <dt className="text-sm text-slate-600">Weighing Delay</dt>
                        <dd className="font-semibold text-slate-900">{lineSettings.weighingDelay || 0} ms</dd>
                      </div>
                      <div className="bg-white border border-slate-200 p-3 rounded">
                        <dt className="text-sm text-slate-600">Zeroing Delay</dt>
                        <dd className="font-semibold text-slate-900">{lineSettings.zeroingDelay || 0} ms</dd>
                      </div>
                      <div className="bg-white border border-slate-200 p-3 rounded">
                        <dt className="text-sm text-slate-600">Zero Threshold Weight</dt>
                        <dd className="font-semibold text-slate-900">{lineSettings.zeroThresholdWeight || 0} LB</dd>
                      </div>
                      <div className="bg-white border border-slate-200 p-3 rounded">
                        <dt className="text-sm text-slate-600">Hopper Capacity</dt>
                        <dd className="font-semibold text-slate-900">{lineSettings.hopperCapacity || 0} LB</dd>
                      </div>
                      <div className="bg-white border border-slate-200 p-3 rounded">
                        <dt className="text-sm text-slate-600">Target Weight</dt>
                        <dd className="font-semibold text-slate-900">{lineSettings.defaultInfeedTargetWeight || 0} LB</dd>
                      </div>
                    </div>
                  </div>

                  {/* Arm Timing Settings */}
                  <div>
                    <h4 className="font-medium text-slate-900 mb-4">Arm Timing Settings (Per Station)</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Station</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Arm Open Delay</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Arm Open Duration</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-200">
                          {(() => {
                            const armSettings = parseArmTimingSettings(lineSettings.armTimingSettings);
                            const settingsArray = Array.isArray(armSettings) ? armSettings : [];
                            
                            if (settingsArray.length === 0) {
                              return (
                                <tr>
                                  <td colSpan={3} className="px-4 py-2 text-sm text-slate-500 text-center">
                                    No arm timing settings configured
                                  </td>
                                </tr>
                              );
                            }
                            
                            return settingsArray.map((station: any, index: number) => (
                              <tr key={index}>
                                <td className="px-4 py-2 text-sm text-slate-900">Station {station.station || index + 1}</td>
                                <td className="px-4 py-2 text-sm text-slate-600">{station.armOpenDelay || 0} ms</td>
                                <td className="px-4 py-2 text-sm text-slate-600">{station.armOpenDuration || 0} ms</td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : isOutfeed ? (
                <>
                  {/* Outfeed Settings */}
                  <div>
                    <h4 className="font-medium text-slate-900 mb-4">Station Weigh Hopper Settings</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="bg-white border border-slate-200 p-3 rounded">
                        <dt className="text-sm text-slate-600">Hopper Capacity</dt>
                        <dd className="font-semibold text-slate-900">{lineSettings.outfeedHopperCapacity || 0} LB</dd>
                      </div>
                      <div className="bg-white border border-slate-200 p-3 rounded">
                        <dt className="text-sm text-slate-600">Cycle Wait Time</dt>
                        <dd className="font-semibold text-slate-900">{lineSettings.outfeedCycleWaitTime || 0} ms</dd>
                      </div>
                      <div className="bg-white border border-slate-200 p-3 rounded">
                        <dt className="text-sm text-slate-600">Weigh Delay</dt>
                        <dd className="font-semibold text-slate-900">{lineSettings.outfeedWeighDelay || 0} ms</dd>
                      </div>
                      <div className="bg-white border border-slate-200 p-3 rounded">
                        <dt className="text-sm text-slate-600">Zero Delay</dt>
                        <dd className="font-semibold text-slate-900">{lineSettings.outfeedZeroDelay || 0} ms</dd>
                      </div>
                      <div className="bg-white border border-slate-200 p-3 rounded">
                        <dt className="text-sm text-slate-600">Zero Threshold Weight</dt>
                        <dd className="font-semibold text-slate-900">{lineSettings.outfeedZeroThresholdWeight || 0} LB</dd>
                      </div>
                      <div className="bg-white border border-slate-200 p-3 rounded">
                        <dt className="text-sm text-slate-600">Breakout Attempts</dt>
                        <dd className="font-semibold text-slate-900">{lineSettings.outfeedBreakoutAttempts || 0}</dd>
                      </div>
                      <div className="bg-white border border-slate-200 p-3 rounded">
                        <dt className="text-sm text-slate-600">Weigh Hopper Close Delay</dt>
                        <dd className="font-semibold text-slate-900">{lineSettings.outfeedPocketBufferHopperCloseDelay || 0} ms</dd>
                      </div>
                    </div>
                  </div>

                  {/* Pocket Close Delays */}
                  <div>
                    <h4 className="font-medium text-slate-900 mb-4">Pocket-Specific Close Delays</h4>
                    <div className="grid grid-cols-3 gap-4">
                      {[0, 1, 2].map((index) => {
                        const delays = parsePocketDelays(lineSettings.pocketCloseDelays);
                        const delayValue = Array.isArray(delays) ? (delays[index] || 1500) : 1500;
                        return (
                          <div key={index} className="bg-white border border-slate-200 p-3 rounded">
                            <dt className="text-sm text-slate-600">Pocket {index + 1} Close Delay</dt>
                            <dd className="font-semibold text-slate-900">{delayValue} ms</dd>
                            <dd className="text-xs text-slate-500">Default: 1500ms</dd>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center text-slate-500">
                  No specific configuration available for this device type
                </div>
              )}

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-900">MQTT Configuration Details</h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>
                        These settings are broadcast to this specific hardware device via MQTT Sparkplug B protocol
                        on Line {lineNumber}. The device will receive only the configuration relevant to its type
                        ({isInfeed ? 'Infeed' : isOutfeed ? 'Outfeed' : 'Unknown'}).
                        {(isInfeed || isOutfeed) && (
                          <span> The total number of stations ({lineSettings?.numberOfStations}) is broadcast so the device knows the complete line configuration.</span>
                        )}
                        {isOutfeed && (
                          <span> Additionally, the specific station numbers this device controls ({(() => {
                            const startStation = (device.station_config - 1) * 4 + 1;
                            const endStation = device.station_config * 4;
                            const stations = [];
                            for (let i = startStation; i <= endStation; i++) {
                              stations.push(i);
                            }
                            return stations.join(', ');
                          })()}) are broadcast for proper station management.</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-slate-500 p-8">
              No configuration data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MqttConfigModal;